// Reviewer category list — fetched lazily from the admin.service proxy
// (`POST /aiChatGateway/getGuardrailCategories`), which forwards to cognilens
// `POST /guardrails/categories`. The cognilens endpoint reads the list from
// ZooKeeper (`guardrailReviewerCategories`) with a v2-schema fallback, so the
// dashboard does not own a canonical copy of the categories.
//
// Caching strategy: a module-level promise dedupes in-flight requests across
// components and a module-level array memoizes the result so subsequent
// component mounts get the list synchronously without another network call.
import { useEffect, useState } from 'react';
import { aiCompanionApi } from './api';

export interface GuardrailCategoryOption {
	id: string;
	label: string;
}

let cachedCategories: GuardrailCategoryOption[] | null = null;
let inFlightPromise: Promise<GuardrailCategoryOption[]> | null = null;

const toOptions = (ids: string[]): GuardrailCategoryOption[] =>
	ids.filter((id) => typeof id === 'string' && id.length > 0).map((id) => ({ id, label: id }));

export const loadGuardrailCategories = async (): Promise<GuardrailCategoryOption[]> => {
	if (cachedCategories) return cachedCategories;
	if (inFlightPromise) return inFlightPromise;
	inFlightPromise = (async () => {
		try {
			const result = await aiCompanionApi.getGuardrailCategories();
			if (result.success && result.data?.categories) {
				cachedCategories = toOptions(result.data.categories);
				return cachedCategories;
			}
			// Network/server failure — return an empty list. The UI shows a
			// placeholder; the next render will retry via useEffect.
			return [];
		} finally {
			inFlightPromise = null;
		}
	})();
	return inFlightPromise;
};

export const useGuardrailCategories = (): GuardrailCategoryOption[] => {
	const [categories, setCategories] = useState<GuardrailCategoryOption[]>(cachedCategories ?? []);
	useEffect(() => {
		if (cachedCategories) {
			if (categories !== cachedCategories) setCategories(cachedCategories);
			return undefined;
		}
		let mounted = true;
		loadGuardrailCategories().then((cats) => {
			if (mounted) setCategories(cats);
		});
		return () => {
			mounted = false;
		};
	}, []);
	return categories;
};
